// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `osnap-query` route body (T8, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `osnap-query` route: given a world-space point (`?osnapX`/`?osnapY`) + a search
// radius (`?osnapRadius`, world units), returns the SINGLE nearest AutoCAD OSNAP candidate
// among endpoint / midpoint / intersection (AutoCAD's documented running-snap priority
// order: endpoint > midpoint > intersection, ties broken by distance then by lexically-
// smaller owner entity id). A BODY only — registered via
// `registerRouteProjector('osnap-query', projectOsnapQuery)`.
//
// REUSES the interactive-canvas OSNAP resolver VERBATIM (`scene3d/snap/resolveObjectSnap`)
// — CLAUDE.md ПРИОРИТЕТ #1 forbids re-deriving CAD math a second time ("не дублируй
// snap-математику"): the priority table, the tie-break, and the pairwise intersection
// sweep (`appendIntersectionCandidates`/`osnapIntersections.ts`) already implement exactly
// AutoCAD's documented behavior for the interactive picker; this route bridges a
// SnapshotEntityRef[] slice into the SAME `RenderableEntity[]` + `SpatialIndex` shape that
// resolver already accepts, rather than re-implementing endpoint/midpoint/intersection math.
//
// WHY colorHex/lineweightWorld/linetype/zOrder on the synthesized RenderableEntity are
// INERT placeholders: grep-verified — `appendCandidatesForEntity` (osnapCandidates.ts) and
// `appendIntersectionCandidates`/`entityCanProduceIntersectShape` (osnapIntersections.ts)
// read only `entity.id`/`entity.geometry`; `RBushSpatialIndex.insert`/`.bulkLoad`
// (spatialIndex.ts) read only `entity.id`/`entity.bounds`. None of the 4 placeholder fields
// ever reaches an osnap computation — this satisfies an over-broad shared TS interface, it
// does NOT fabricate CAD truth (`id`/`spaceId`/`layer`/`type`/`geometry` ARE the real
// per-entity values copied verbatim off the snapshot).
//
// DEP-GRAPH NOTE (ПРИОРИТЕТ #2): this is the FIRST `ai/` → `scene3d/` import edge in the
// codebase. `scene3d/snap/*` + `scene3d/spatialIndex.ts` + `scene3d/types.ts` are
// THREE-free / DOM-free / editorStore-free pure modules (verified by reading them — no
// `three`/`editorStore` import in any of the three); this leaf imports ONLY those + the
// seam, so no new dependency CYCLE is introduced. `npm run deps:graph:check` must stay
// green after this file lands; `npm run deps:graph` regenerates the committed graph doc
// when the tool reports the new edge as undocumented.
//
// Local Codex round-2 P2 + own-review finding — ADDS two more leaf value edges:
// `scene3d/adapters/geom/revisionCloudGeometry.ts` (`buildRevisionCloudSceneGeometry`, the
// shared REVCLOUD scallop tessellation the render path already uses) and
// `scene3d/adapters/sceneGeometryWorldCorrection.ts` (`correctSceneGeometryToWorld`, the
// shared offset-space → true-world corrector `state/captureSnapshot.ts`'s
// `blockFlattenedChildren` facet also reuses). Both are pure leaves under `scene3d/adapters/`
// (no import back into `ai/` or `state/`) — same direction as the existing edges above, no
// new cycle. `npm run deps:graph:check` cycles=0.
//
// @codex PR #785 round 16 — ADDS two more leaf value edges of the same shape, for the group-39
// THICKNESS sweep: `scene3d/adapters/geom/polylineThicknessSweep.ts` (`thicknessSweepVector`, THE
// resolution of group 39 into the world vector `t·N̂`, read off `drawing-model`'s own
// `withThicknessAlongExtrusion` — the same call `scene3d/adapters/typed/polylineFromEntity.ts`
// makes, so the agent's geometry cannot disagree with the canvas about which way the sweep goes)
// and `scene3d/snap/osnapSweptRuns.ts` (the one projection of that sweep onto the SNAP surface,
// shared with `snap/osnapCandidates.ts` and `snap/osnapIntersections.ts`). Both are pure leaves
// (`drawing-model` + sibling geom modules + `scene3d/types`; no `three`, no DOM, no import back
// into `ai/`/`state/`) — same direction as the edges above, no new cycle.
//
// @codex PR #785 round 7 — ADDS a third leaf value edge of the same shape:
// `scene3d/adapters/geom/polylineWidthBand.ts` (`polylineCentrelineSamples`, THE tessellation
// of a bulged centreline, in the entity's own OCS plane). It is the call the canvas batcher,
// the pick (`scene3d/raycast/distance.ts`) and the INTERSECTION snap
// (`scene3d/snap/osnapIntersections.ts`) already share, so reading it here is what keeps the
// agent's query AABB on the curve the canvas draws instead of a fourth private reconstruction
// of where a bulged span goes. Pure leaf (`drawing-model` + `../../types` + its two sibling
// geom modules; no `three`, no DOM, no import back into `ai/`/`state/`) — same direction, no
// new cycle.
//
// NOT an `aiCallable` action (CLAUDE.md: "AI drafting не имеет права писать сущности
// напрямую, он может только предлагать и исполнять команды через безопасный контур") —
// this route is READ-ONLY informational context for an agent deciding WHERE to snap a
// proposed command; it is never registered in `packages/ai-commands`' manifest / action-
// target-command maps.
// ═══════════════════════════════════════════════════════════════════════════

import {
  heavyPolylineRuns,
  heavyPolylineWcsResolver,
} from "@dxf-ai-editor/cad-core";
import {
  fitPointsToControlPoints,
  isDefaultExtrusion,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";

import {
  QUERY_VALIDATION_CODES,
  type RouteProjector,
  type SceneSnapshotRef,
  type SnapshotEntityRef,
  type StateReadPage,
} from "../sceneSummaryProjector";
import { polylineCentrelineSamples } from "../../scene3d/adapters/geom/polylineWidthBand";
import { thicknessSweepVector } from "../../scene3d/adapters/geom/polylineThicknessSweep";
import { buildRevisionCloudSceneGeometry } from "../../scene3d/adapters/geom/revisionCloudGeometry";
import { correctSceneGeometryToWorld } from "../../scene3d/adapters/sceneGeometryWorldCorrection";
import { osnapPolylineSweptRuns } from "../../scene3d/snap/osnapSweptRuns";
import {
  MODE_PRIORITY,
  resolveObjectSnap,
  type OsnapCandidate,
  type OsnapMode,
} from "../../scene3d/snap/resolveObjectSnap";
import { createSpatialIndex } from "../../scene3d/spatialIndex";
import type { RenderableEntity, SceneGeometry, Vec3 } from "../../scene3d/types";

/**
 * Degrees → radians. DXF ARC (group 50/51) stores `startAngle`/`endAngle` in DEGREES
 * (`arcEntitySchema`, model.ts) — confirmed by `boundaryDetection.ts`'s
 * `degToRad(entity.startAngle)` and `sceneGeometryBuilders.ts`'s
 * `buildArcSceneGeometry(startDeg, endDeg)`, both of which convert the SAME field before use.
 * `SceneGeometry.arc.start`/`.end` (what `osnapCandidates.ts` consumes via `Math.cos`/`Math.sin`
 * directly) ARE radians — convert here, once, at the adapter boundary. Mirrors the per-file
 * `DEG_TO_RAD` pattern used by `sceneGeometryBuilders.ts` (no shared exported constant to reuse).
 */
const DEG_TO_RAD = Math.PI / 180;

/**
 * The 3 modes T8 supports (MASTERPLAN §T8 / AI-BRIDGE-EDIT-TASKS.md T8 — "ближайший
 * конец/середина/пересечение к точке"). Deliberately NOT configurable via a query param —
 * the task names exactly these 3; a mode-selection knob would be undocumented scope growth.
 */
const OSNAP_QUERY_MODES: ReadonlySet<OsnapMode> = new Set(["endpoint", "midpoint", "intersection"]);

/**
 * Entity kinds whose `SnapshotEntityGeometry` contributes to endpoint/midpoint
 * (`appendCandidatesForEntity`) or intersection (`appendIntersectionCandidates`: LINE /
 * LWPOLYLINE-segment / CIRCLE / ARC only, per `osnapIntersections.ts`'s own docstring).
 * Point/Text/MText/Dimension/Multileader/Hatch/Wipeout/ArrayRect/Viewport are excluded —
 * under this route's FIXED 3-mode set they only ever emit modes (node/insertion/
 * center-only-shapes) this route never asks for, so wiring a geometry adapter for them would
 * be dead code, never a candidate. NOT a partial-support cut: it is the exact, principled set
 * `appendCandidatesForEntity`'s switch already treats as shape-bearing for endpoint/midpoint,
 * plus the 4 STRAIGHT+CONIC kinds `appendIntersectionCandidates` supports, plus the 2 kinds
 * (`"Revision Cloud"` / `"Block"`) that get DEDICATED handling in the route body, outside the
 * "shape-bearing" / "STRAIGHT+CONIC" reasoning above (GitHub Codex PR #528 Findings 2+4) — a
 * `"Revision Cloud"` maps through `buildRevisionCloudSceneGeometry` (the SAME scalloped-curve
 * tessellation the render path uses — `toSceneGeometry`'s `"Revision Cloud"` arm below; local
 * Codex round-2 P2, NOT the straight-vertex `polylineFacetToSceneGeometry` a plain LWPOLYLINE
 * gets, since a REVCLOUD is visually/interactively a scalloped curve, not its authored anchor
 * polygon); a `"Block"` INSERT is never itself geometry-adapted — its FLATTENED CHILDREN,
 * `ref.blockFlattenedChildren`, are the candidates, mirroring how the interactive canvas OSNAP
 * resolver snaps to flattened block-child renderables rather than the INSERT itself.
 *
 * `"Polyline"` (the legacy heavy POLYLINE) and `"OLE Frame"` join the set for the
 * same shape-bearing reason LWPOLYLINE does: a 2-D / 3-D heavy polyline IS an
 * endpoint/midpoint-bearing curve, and an OLE frame's two WCS corners define a
 * rectangle whose corners and edge midpoints AutoCAD snaps to (the embedded
 * raster is never decoded — see `oleFrameEntitySchema`). A polygon / poly-face
 * MESH polyline is admitted by the SAME entry: it is shape-bearing too, and
 * `toSceneGeometry` adapts it into the first-class `mesh-wire` geometry the
 * shared snap resolver already consumes run-by-run (@codex PR #785 round 4, F3
 * — it used to be dropped there, so the interactive mesh OSNAP never reached
 * this route).
 *
 * `"Table"` (ACAD_TABLE) joins for the SAME reason `"Block"` does, and travels
 * the SAME path (@codex PR #785 round 2, F2). An ACAD_TABLE literally IS a
 * block reference — its first subclass is `AcDbBlockReference`, group 2 names
 * the anonymous `*T<N>` block holding the table's fully-rendered cell MTEXT and
 * LINE grid, and 10/20/30 is the insertion point — so its snap targets are that
 * block's FLATTENED CHILDREN (`ref.blockFlattenedChildren`, the same facet an
 * INSERT uses), never the table's bare insertion point. Those are the SAME
 * graphics the render path (`scene3d/adapters/typed/tableFromEntity.ts`),
 * `cad-core`'s exact-selection pass (`select.ts`) and the bounds walk
 * (`bounds.ts`) already flatten through the ONE shared
 * `drawing-model::tableAsBlockReference` projection — this route derives
 * nothing new about "what a table occupies". It is NOT table EDITING: cell
 * values / formulas / styles / merges stay out of scope exactly as
 * `tableEntitySchema`'s own boundary doc says.
 *
 * An earlier revision of this PR excluded `"Table"` on the grounds that only a
 * `"Block"` INSERT gets `blockFlattenedChildren` populated, so a table could
 * only ever have contributed its insertion point — a WRONG answer rather than a
 * partial one. That reasoning held only while the children were unavailable;
 * see `projectOsnapQuery`'s block-child branch below for the PRODUCER-side
 * status of that facet.
 */
const OSNAP_QUERY_ENTITY_TYPES: ReadonlySet<string> = new Set([
  "Line",
  "Circle",
  "Arc",
  "Ellipse",
  "Spline",
  "LWPOLYLINE",
  "Revision Cloud",
  "Block",
  "Polyline",
  "OLE Frame",
  "Table",
]);

/**
 * The entity types that ARE block references and therefore contribute their FLATTENED CHILDREN
 * (`ref.blockFlattenedChildren`) rather than a `toSceneGeometry` adaptation of their own record:
 * a `"Block"` INSERT (GitHub Codex PR #528 Finding 4) and a `"Table"` ACAD_TABLE, whose anonymous
 * `*T<N>` graphics block carries its cell MTEXT and LINE grid (@codex PR #785 round 2, F2). Both
 * are a SUBSET of `OSNAP_QUERY_ENTITY_TYPES` — this set only says WHICH candidate path an
 * already-admitted entity takes.
 */
const BLOCK_BACKED_ENTITY_TYPES: ReadonlySet<string> = new Set(["Block", "Table"]);

/**
 * The `SceneGeometry.kind` values (LOWERCASE — a DIFFERENT casing convention from
 * `SnapshotEntityRef.type`'s uppercase "Line"/"Circle"/…) a flattened block-child
 * `RenderableEntity` must carry to be a candidate for this route's fixed 3-mode set
 * (GitHub Codex PR #528 Finding 4) — the SAME kind set `OSNAP_QUERY_ENTITY_TYPES` principle
 * covers for top-level entities (shape-bearing for endpoint/midpoint, or one of the 4
 * STRAIGHT+CONIC intersection kinds). A block child's own SceneGeometry is already fully
 * built by `blockFromEntity` (it went through the real typed adapters), so no `toSceneGeometry`
 * re-adaptation is needed — only this kind filter.
 */
const OSNAP_QUERY_CHILD_GEOMETRY_KINDS: ReadonlySet<SceneGeometry["kind"]> = new Set([
  "line",
  "circle",
  "arc",
  "ellipse",
  "spline",
  "polyline",
  // A heavy POLYLINE MESH child (@codex PR #785 round 4, F3). `mesh-wire` is
  // shape-bearing by exactly the same rule the kinds above are: `osnapCandidates.ts`
  // snaps every wire run by the SAME segment math a `polyline` run gets, and
  // `osnapCapabilities.ts` lists endpoint/midpoint among its modes. Omitting it
  // dropped a mesh nested in a BLOCK from the agent-facing query even though the
  // interactive canvas snaps it.
  "mesh-wire",
]);

/**
 * Convert a `{x,y,z?}`-shaped facet point into a `Vec3` (z defaults to 0 — `SceneGeometry`
 * requires a numeric z; osnap math is 2D-XY throughout — `osnapCandidates.ts`'s own
 * `toVec2` drops z — so the default never affects the RESULT, only satisfies the type).
 */
function toVec3(p: { readonly x: number; readonly y: number; readonly z?: number }): Vec3 {
  return { x: p.x, y: p.y, z: p.z ?? 0 };
}

/**
 * Adapt one entity's `SnapshotEntityGeometry` (T1's canonical per-type facet — the AI
 * contour's OWN geometry projection, NOT the renderer's) into the `scene3d/snap` resolvers'
 * `SceneGeometry` shape. A PURE RESHAPE (rename/regroup fields) — NOT a re-derivation of any
 * geometry math. Returns `null` for a kind this route does not consider (defensive; the
 * caller already filtered by `OSNAP_QUERY_ENTITY_TYPES`, so this is unreachable in practice,
 * mirroring the geometry-facet's own documented `?? null` deferred-geometry guard).
 *
 * Reads `ref.rawGeometry` (GitHub Codex PR #528 Finding 1) — the UNCAPPED internal geometry
 * accessor (`captureSnapshot.ts toEntityRef`'s `rawGeometry` getter, backed by
 * `toEntityGeometryUncapped`) — NOT `ref.geometry` (the `entities` route's CAPPED public
 * projection). `ref.geometry` applies `capGeometry`'s per-entity byte cap to protect the
 * `entities` route's `?fullField=geometry` WIRE payload; this route's output is a tiny
 * `{mode,point,ownerEntityId,distanceWorld}` that never re-serializes the raw geometry over
 * the wire, so that wire-protection cap does not apply here — reading the capped accessor
 * would silently drop a colossal Spline/LWPOLYLINE/Hatch entity's real geometry (a
 * `TruncatedGeometry` marker instead), producing NO candidate even though the live model has
 * real, findable geometry near the query point.
 *
 * EXPORTED for the route's own test matrix
 * (`__tests__/osnapQueryPolylineElevation.property.test.ts`) — NOT a public API
 * widening. The OSNAP pipeline this feeds is 2-D END TO END (`osnapCandidates.ts`'s `toVec2`
 * drops Z, `OsnapCandidate.world` is a `Vec2`, and this route's wire item is `{x,y}`), so the Z
 * this adapter writes into `SceneGeometry` is NOT observable through `projectStateRead`. The
 * per-subtype Z rule (now `cad-core`'s `heavyPolylineWcsResolver`, not a private copy here) is
 * still a real correctness contract — the geometry handed to `resolveObjectSnap` must describe the
 * same plane the renderer draws — so it is pinned where it IS observable: on this function's
 * output. The OCS lift the SAME resolver applies IS observable in x/y, and is pinned there too.
 */
export function toSceneGeometry(ref: SnapshotEntityRef): SceneGeometry | null {
  const g = ref.rawGeometry; // triggers the lazy memoized UNCAPPED getter (captureSnapshot.ts toEntityRef).
  if (g === undefined) {
    return null;
  }
  switch (g.kind) {
    case "Line":
      return { kind: "line", a: toVec3(g.start), b: toVec3(g.end) };
    case "Circle":
      return { kind: "circle", center: toVec3(g.center), radius: g.radius };
    case "Arc":
      // DXF ARC has no direction flag — always CCW (ccw: true) by convention (mirrors every
      // other ARC→SceneGeometry adapter in the codebase, e.g. scene3d/adapters/typed). `g.startAngle`/
      // `g.endAngle` are DEGREES (see the `DEG_TO_RAD` doc comment above) — convert to radians for
      // `SceneGeometry.arc`, which `osnapCandidates.ts` consumes directly with `Math.cos`/`Math.sin`.
      return {
        kind: "arc",
        center: toVec3(g.center),
        radius: g.radius,
        start: g.startAngle * DEG_TO_RAD,
        end: g.endAngle * DEG_TO_RAD,
        ccw: true,
      };
    case "Ellipse":
      return {
        kind: "ellipse",
        center: toVec3(g.center),
        majorAxis: toVec3(g.majorAxisEndpoint),
        ratio: g.ratio,
        start: g.startParam,
        end: g.endParam,
        extrusion: toVec3(g.extrusion),
      };
    case "Spline": {
      // A fit-point-authored spline has EMPTY controlPoints + populated fitPoints (DXF 74>0,
      // 73=0) — derive usable control points + knots via the ONE-TRUTH interpolator (the SAME
      // call scene3d/adapters/typed/splineFromEntity.ts makes for the render/pick path) rather
      // than silently emitting an empty/degenerate curve (CLAUDE.md ПРИОРИТЕТ #1).
      const fitMode = g.controlPoints.length === 0 && g.fitPoints.length > 0;
      const source = fitMode
        ? fitPointsToControlPoints([...g.fitPoints], g.startTangent, g.endTangent, g.degree)
        : { controlPoints: g.controlPoints, knots: g.knots };
      // Local Codex round-2 P3: a schema-degenerate spline (BOTH controlPoints AND fitPoints
      // empty — no real DXF authors one, but the schema allows it) would otherwise reach
      // `bboxFromSceneGeometry` with an empty point list → `boundsFromPoints([])` →
      // `{minX:+Infinity, maxX:-Infinity, ...}` — an inverted/non-finite bbox bulk-loaded into
      // the RBush spatial index. Treat "no derivable geometry" the same as an unsupported kind
      // (return null, skipped by the caller) rather than indexing a broken bbox.
      if (source.controlPoints.length === 0) {
        return null;
      }
      return {
        kind: "spline",
        controlPoints: source.controlPoints.map(toVec3),
        knots: [...source.knots],
        degree: g.degree,
        // Fit mode: the interpolator already consumed the tangents as boundary conditions —
        // emitting tangentVectors again would double-apply the constraint downstream (mirrors
        // splineFromEntity.ts's `!fitMode && …` gate).
        ...(!fitMode && g.startTangent !== undefined && g.endTangent !== undefined
          ? { tangentVectors: { start: toVec3(g.startTangent), end: toVec3(g.endTangent) } }
          : {}),
      };
    }
    case "LWPOLYLINE":
      return polylineFacetToSceneGeometry(g.vertices, g.closed);
    case "Polyline": {
      // Legacy heavy POLYLINE, adapted through the ONE shared topology
      // `cad-core`'s `heavyPolylineTopology.ts` owns — `heavyPolylineRuns` (WHICH
      // records are visible geometry, in what order, which spans are curved, and
      // for a MESH which rows / columns / face edges the wireframe has) plus
      // `heavyPolylineWcsResolver` (WHERE each of those records lands in WCS).
      // Exactly the two calls the render adapter
      // (`scene3d/adapters/typed/polylineFromEntity.ts`), the picker
      // (`cad-core::selectLeaf.ts`) and the vector-PDF plot make, so the agent
      // can never be handed a snap point that is not on the drawn curve. The
      // facet carries every channel those two functions read — group-70 flags,
      // per-record flags / face indices / location / bulge, elevation, the mesh
      // M/N counts and the group-210 extrusion — verbatim off the entity, which
      // is why they take a STRUCTURAL source rather than `PolylineEntity`.
      //
      // Two defects this replaces (@codex PR #785 round 4, F2 + F3):
      //
      //   • the OCS lift was MISSING here. A 2-D heavy polyline's group-10/20
      //     values are OBJECT coordinates in the plane of its group-210
      //     extrusion; copying them verbatim (the previous `v.location.x/y`)
      //     returned endpoints and midpoints at a different XY position from the
      //     rendered curve — for the commonplace `(0,0,-1)` normal, mirrored in
      //     X. The resolver is the same Arbitrary-Axis basis the renderer, the
      //     WCS bounds, viewport membership and the plot all lift through, and
      //     it also owns the per-SUBTYPE Z rule (a 2-D polyline is planar at the
      //     ENTITY elevation; a 3-D polyline's per-vertex Z is real WCS data),
      //     which this module used to restate in a private helper;
      //   • a polygon / poly-face MESH was dropped outright. `SceneGeometry` now
      //     has a first-class `mesh-wire` kind whose runs the SHARED snap
      //     resolver consumes (`osnapCandidates.ts`'s `mesh-wire` arm snaps every
      //     run by the same rule a polyline run gets), so the interactive mesh
      //     OSNAP fix reaches the agent-facing query through the same path
      //     instead of a parallel mesh unpacker.
      const pointOf = heavyPolylineWcsResolver(g);
      const runs = heavyPolylineRuns(g);
      // The subtype is decoded from the group-70 FLAGS by the same one-truth
      // `polylineSubtype` the topology itself calls, NOT read off the facet's
      // precomputed `subtype` label. `captureEntityGeometry.ts` derives that
      // label from these very flags so the two always agree for a real snapshot;
      // decoding once here is what guarantees this arm's mesh/curve choice can
      // never diverge from the runs `heavyPolylineRuns` just produced.
      const subtype = polylineSubtype(g);
      if (subtype === "polygon-mesh" || subtype === "polyface-mesh") {
        // A mesh's wireframe is several DISJOINT runs belonging to ONE entity —
        // which `polyline` (a single connected stroke) cannot express and
        // `mesh-wire` exists for. A run of fewer than 2 indices has no edge, the
        // same `< 2` drop the render adapter applies.
        const wireRuns = runs
          .filter((run) => run.indices.length >= 2)
          .map((run) => ({
            points: run.indices.map((i) => pointOf(i)),
            closed: run.closed,
          }));
        if (wireRuns.length === 0) {
          return null;
        }
        return { kind: "mesh-wire", runs: wireRuns };
      }
      // A 2-D / 3-D polyline is exactly ONE run over the visible records (the
      // SPLINE-fit control FRAME excluded — it is not drawn, so snapping it
      // would hand the agent points that exist nowhere on screen, plus a phantom
      // span from the end of the curve back to the frame).
      const run = runs[0];
      if (run === undefined) {
        return null;
      }
      const vertices = run.indices.map((i, k) => {
        const p = pointOf(i);
        // `run.bulges` is the per-SPAN bulge list travelling WITH the filtered
        // record order (already pinned to 0 for every subtype that cannot bow
        // a span — group 42 is a 2D-polyline field), so no `subtype === "2d"`
        // gate or `?? 0` fallback is re-derived here.
        return { x: p.x, y: p.y, z: p.z, bulge: run.bulges[k] ?? 0 };
      });
      // THE PLANE the entity's own 2D construction belongs to (@codex PR #785
      // round 7, FINDING A). `pointOf` has already lifted the vertices OUT of
      // it, and an OCS lift maps a span's CHORD correctly but NOT its BOW: a
      // bulge (group 42) is measured in the OCS plane, so for extrusion
      // `(0,1,0)` — where the OCS y axis IS world Z — the 5-unit bow of a
      // `(0,0) → (10,0)` bulge-1 semicircle belongs on world Z, and the canvas
      // puts it there (`scene3d/adapters/typed/polylineFromEntity.ts` declares
      // exactly this field, and `adapters/geom/polylineWidthBand.ts::
      // polylineCentrelineSamples` builds the arc in the plane). Dropping the
      // field here left the agent searching a PHANTOM semicircle in the WCS XY
      // plane the entity never enters, five units from the drawn straight
      // projection — a wrong snap point on ordinary geometry.
      //
      // Carrying the field is the WHOLE fix, not a re-derivation: the shared
      // resolver already reads it (`snap/osnapCandidates.ts`'s `handlePolyline`
      // passes `g.extrusion` into `emitPolylineRunCandidates`, which evaluates a
      // bulged span through `polylinePlane` exactly as the batcher, the pick and
      // the INTERSECTION snap do), so the agent now reads the SAME curve the
      // canvas draws rather than a fourth private reconstruction of it.
      //
      // Gated as `polylineFromEntity` gates it, minus the half that cannot
      // apply here: only the 2-D subtype (a 3-D polyline stores REAL WCS
      // vertices and the shared topology reports no OCS for it, so attaching a
      // plane would tilt geometry that was never in one), only a non-default
      // group 210 (every ordinary polyline keeps the byte-identical geometry it
      // had), and only when there is something IN-PLANE to build. For the
      // renderer that is "a width or a bulge"; this route emits no `widths` at
      // all (the band is a drawing concern, never a snap one — see
      // `polylineFacetToSceneGeometry`), so here it is the BULGE alone. The two
      // gates therefore differ only where the field would be inert in both:
      // with no bulge, `polylineCentrelineSamples` returns its input untouched
      // and every span takes `emitPolylineRunCandidates`' flat branch.
      const tilted =
        subtype === "2d" &&
        !isDefaultExtrusion(g.extrusion) &&
        vertices.some((v) => v.bulge !== 0);
      // THE GROUP-39 THICKNESS SWEEP (@codex PR #785 round 16, the OSNAP
      // fan-out). Group 39 is GEOMETRY — Autodesk's DXF reference defines it as
      // the EXTRUSION of the entity along its extrusion direction, producing a
      // surface — and since round 15 the canvas PAINTS that surface for a heavy
      // 2-D polyline (`scene3d/adapters/typed/polylineFromEntity.ts`). This
      // route dropped the field, so the agent's geometry described the base
      // half only: on the round-16 witness (extrusion `(1,1,1)`, thickness 5 on
      // `(0,0) → (10,0)`) `bboxFromSceneGeometry` answered maxX 0 while the
      // painted top vertex sits at x = 2.886751345948129, so an `osnap-query`
      // at that visible vertex never overlapped the AABB, the spatial index
      // never returned the entity, and the exact resolver never ran — no point
      // at all, the strictly worse failure `polylineCentrelineSamples` fixed
      // for bulges in round 7.
      //
      // The GATE is `polylineFromEntity`'s verbatim: the 2-D subtype only
      // (Autodesk's OCS section states that of the entities that "do not lie in
      // a particular plane" — 3DPOLYLINE and the MESH subtypes among them —
      // "only lines and points can be extruded", so fabricating a surface for
      // them would describe geometry the file does not contain), and the VECTOR
      // is resolved by the SAME shared call the adapter makes, never a second
      // normalization of group 210. `null` for every thickness-less polyline,
      // which is almost all of them.
      //
      // ONLY the heavy `"Polyline"` arm carries it, because only that arm's
      // entity is painted swept today: the LWPOLYLINE / REVCLOUD / OLE-frame
      // adapters still drop group 39 (the named follow-up in
      // `scene3d/adapters/geom/polylineThicknessSweep.ts`' header). Declaring it
      // here for them would offer the agent snap points on a surface the canvas
      // does not draw — snappable OUTSIDE drawn, the direction this round is
      // closing, not widening.
      const sweep =
        subtype === "2d"
          ? thicknessSweepVector(g.thickness, g.extrusion)
          : null;
      return polylineFacetToSceneGeometry(
        vertices,
        run.closed,
        tilted ? g.extrusion : undefined,
        sweep,
      );
    }
    case "OLE Frame":
      // The OLE frame's snap geometry is the frame RECTANGLE its two WCS
      // corners define — the same rectangle the renderer draws (the embedded
      // raster is never decoded, see `oleFrameEntitySchema`). Corner and
      // midpoint snaps on that rectangle are what AutoCAD offers on an OLE
      // object whose server cannot render it.
      return polylineFacetToSceneGeometry(
        [
          { x: g.upperLeft.x, y: g.upperLeft.y, z: g.upperLeft.z, bulge: 0 },
          { x: g.lowerRight.x, y: g.upperLeft.y, z: g.upperLeft.z, bulge: 0 },
          { x: g.lowerRight.x, y: g.lowerRight.y, z: g.lowerRight.z, bulge: 0 },
          { x: g.upperLeft.x, y: g.lowerRight.y, z: g.lowerRight.z, bulge: 0 },
        ],
        true,
      );
    case "Revision Cloud": {
      // Local Codex round-2 P2 — REVCLOUD is NOT the straight authored-vertex polygon: AutoCAD
      // (Autodesk DXF reference) scallops every segment into a half-circle arc bulge, and
      // `scene3d/adapters/geom/revisionCloudGeometry.ts`'s `buildRevisionCloudSceneGeometry` is the
      // ONE-TRUTH tessellation the render path (`scene3d/adapters/typed/revisionCloudFromEntity.ts`)
      // already uses. Mapping the raw vertices through the straight-polyline helper (the pre-fix
      // behaviour) returned chord-line midpoints/intersections that are not on the visible cloud
      // curve — reusing the shared builder here instead (dup:check — one domain truth for "how a
      // REVCLOUD tessellates") fixes that at the source rather than re-deriving the bulge math.
      //
      // `entity.elevation` (DXF group 38 — REVCLOUD is an LWPOLYLINE on disk) is the common z of
      // every vertex — mirrors `revisionCloudFromEntity.ts`'s own elevation read (a documented
      // superset vs the legacy bag path, which always renders at z=0); the facet's `vertices` carry
      // x/y only (no per-vertex z), matching that adapter's `GeomPolylineVertex` shape.
      const elevation = Number.isFinite(g.elevation) ? g.elevation : 0;
      const result = buildRevisionCloudSceneGeometry(
        g.vertices.map((v) => ({ x: v.x, y: v.y, z: elevation })),
        g.closed,
        g.approximateArcLength, // the SAME field `revisionCloudFromEntity.ts` feeds the builder — NOT arcChordLength (a distinct DXF field, unused by the tessellation).
      );
      // `buildRevisionCloudSceneGeometry` owns its OWN degenerate-empty guard (< 2 offset vertices
      // OR < 2 sampled points → null) — the SAME guard class the Spline/LWPOLYLINE arms apply above
      // (local Codex round-2/round-3 P3), so no separate zero-vertex check is needed here.
      if (result === null) {
        return null;
      }
      // `buildRevisionCloudSceneGeometry` applies `applyOffset` INSIDE the builder per vertex (its
      // own module doc comment) — RENDERER-ONLY offset-space, the exact same concern
      // `state/captureSnapshot.ts`'s `blockFlattenedChildren` facet corrects for block children.
      // Correct back to TRUE document-world coordinates via the SAME shared corrector (one-truth
      // inverse-offset choke point, `sceneGeometryWorldCorrection.ts`) before this becomes the
      // route's output geometry. `result.bounds` is NOT read here — `toRenderableEntity` below
      // recomputes bounds via `bboxFromSceneGeometry` on this ALREADY-corrected geometry, so a
      // stale offset-space `result.bounds` is simply discarded rather than needing its own correction.
      return correctSceneGeometryToWorld(result.geometry);
    }
    default:
      return null;
  }
}

/**
 * `LWPOLYLINE` → `polyline` `SceneGeometry` mapper — the straight/bulged authored-vertex shape (a
 * plain LWPOLYLINE is NOT a scalloped-arc entity, unlike REVCLOUD; local Codex round-2 P2 moved
 * `"Revision Cloud"` OFF this helper onto `buildRevisionCloudSceneGeometry`'s scalloped-curve
 * tessellation — see that `toSceneGeometry` arm above — so this helper now has exactly ONE caller).
 * Same degenerate-empty guard as the Spline arm (local Codex round-2 P3): a schema-degenerate facet
 * with zero vertices would otherwise index an inverted/non-finite `boundsFromPoints([])` bbox —
 * return `null` (skipped by the caller) instead.
 */
function polylineFacetToSceneGeometry(
  vertices: readonly {
    readonly x: number;
    readonly y: number;
    readonly z?: number;
    readonly bulge: number;
  }[],
  closed: boolean,
  extrusion?: Vec3,
  thicknessSweep?: Vec3 | null,
): SceneGeometry | null {
  if (vertices.length === 0) {
    return null;
  }
  return {
    kind: "polyline",
    // `z ?? 0` — an LWPOLYLINE facet vertex is a flat `{x, y, bulge}` tuple
    // (the entity's single elevation lives elsewhere), while a heavy POLYLINE
    // VERTEX carries a REAL per-vertex Z that a 3-D polyline depends on. One
    // mapper serves both rather than a second near-identical copy.
    points: vertices.map((v) => ({ x: v.x, y: v.y, z: v.z ?? 0 })),
    closed,
    bulges: vertices.map((v) => v.bulge),
    // The OCS plane a BULGED span's bow belongs in, when the caller's points
    // were lifted out of one — see the `"Polyline"` arm's own note. Omitted
    // (not `undefined`-valued) for every caller that has no plane to declare,
    // so an ordinary polyline's geometry object keeps the exact shape it had.
    // `widths` are deliberately NOT emitted by this route at all: the width
    // BAND is a drawing concern, and AutoCAD snaps a wide polyline on its
    // CENTRELINE, which is what `osnapCandidates.ts` reads from `points`.
    ...(extrusion !== undefined ? { extrusion } : {}),
    // The DXF group-39 thickness, already resolved into the world vector `t·N̂`
    // — see the `"Polyline"` arm's own note for why only that caller supplies
    // one. Omitted (not `undefined`-valued) for every other caller, so an
    // ordinary polyline's geometry object keeps the exact shape it had.
    ...(thicknessSweep ? { thicknessSweep } : {}),
  };
}

/** A `{x,y}` point — the minimal shape the shared min/max reducer needs. */
interface Point2 {
  readonly x: number;
  readonly y: number;
}

type Bounds = { readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number };

/**
 * The ONE-TRUTH min/max-over-points reducer every `bboxFromSceneGeometry` arm calls (with the
 * right point set per kind) instead of a copy-pasted min/max block per kind (dup:check — one
 * domain truth: "the AABB of a point set").
 */
function boundsFromPoints(points: readonly Point2[]): Bounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * A conservative (never-too-small) world-space AABB for a `SceneGeometry` shape — used ONLY
 * to seed the spatial index's bbox prefilter (`RBushSpatialIndex`). It does not need to be
 * TIGHT: `resolveObjectSnap`'s per-point math is exact regardless of the bbox; a loose bbox
 * only costs a slightly wider candidate window, never a wrong result. Circle/arc/ellipse use
 * the FULL circle/ellipse bound (center ± radius / ± major-axis magnitude) even for a partial
 * arc — safe (never excludes a true point), simple, and mirrors how `RBushSpatialIndex`
 * bulk-loads bounds today (a bbox prefilter, not the final hit-test).
 *
 * EXPORTED for the route's own test matrix
 * (`__tests__/osnapQueryBulgedPolyline.metamorphic.test.ts`) — NOT a public API widening, the
 * same reason `toSceneGeometry` above is exported. The box is observable through
 * `projectStateRead` only as a PRESENCE/ABSENCE (an entity whose box misses the query window is
 * never retrieved and the exact resolver behind it never runs), which makes an end-to-end
 * assertion able to say "this snap was missing" but not "by how much the box was short".
 * @codex PR #785 round 7 FINDING B was exactly a too-small box, so the containment relation is
 * pinned where it IS measurable: on this function's output.
 */
export function bboxFromSceneGeometry(g: SceneGeometry): Bounds {
  switch (g.kind) {
    case "line":
      return boundsFromPoints([g.a, g.b]);
    case "circle":
      return boundsFromPoints([
        { x: g.center.x - g.radius, y: g.center.y - g.radius },
        { x: g.center.x + g.radius, y: g.center.y + g.radius },
      ]);
    case "arc":
      // Full-circle bound (not the partial-arc sweep) — a safe over-approximation.
      return boundsFromPoints([
        { x: g.center.x - g.radius, y: g.center.y - g.radius },
        { x: g.center.x + g.radius, y: g.center.y + g.radius },
      ]);
    case "ellipse": {
      // `ratio <= 1` always (DXF ELLIPSE minor/major ratio) — the major-axis magnitude bounds
      // BOTH axes, so a square of that half-extent around the center is a safe over-approximation.
      const extent = Math.hypot(g.majorAxis.x, g.majorAxis.y);
      return boundsFromPoints([
        { x: g.center.x - extent, y: g.center.y - extent },
        { x: g.center.x + extent, y: g.center.y + extent },
      ]);
    }
    case "spline":
      return boundsFromPoints(g.controlPoints);
    case "polyline": {
      // The box of what is actually DRAWN, read through the SAME call the
      // canvas batcher, the pick (`raycast/distance.ts`) and the INTERSECTION
      // snap (`snap/osnapIntersections.ts`) tessellate a bulged centreline
      // with — and the same rule `typed/polylineFromEntity.ts` takes its own
      // render AABB by (@codex PR #785 round 7, FINDING B).
      //
      // The endpoint hull alone is NOT a superset of a BULGED polyline: a
      // `(0,0) → (10,0)` bulge-1 semicircle bows five units clear of it, so an
      // `osnap-query` with a small radius around the visible apex never
      // overlapped this AABB, the spatial index never returned the entity, and
      // the exact resolver behind it — which does know the arc — never ran at
      // all. The agent simply could not snap to the apex of any bulged
      // polyline. That is a strictly worse failure than a wrong point: there
      // was no point.
      //
      // `polylineCentrelineSamples` returns its INPUT ARRAY untouched for
      // every bulge-free run, so every straight polyline — and every geometry
      // this route built before bulges were carried — keeps the byte-identical
      // box it had. For a TILTED run it walks the arc in the entity's own OCS
      // plane and lifts the samples, so the box follows the curve into that
      // plane instead of claiming a phantom extent in the lifted XY.
      //
      // The samples are CHORDS of the arc, so an exact arc point can sit up to
      // one sagitta (r·(1−cos(π/128)) ≈ 3e-4·r at the shared
      // `BAND_ARC_SAMPLES_PER_TURN`) outside this box. That is deliberate
      // parity, not a gap: it is exactly the box the canvas indexes the same
      // entity by, and `resolveObjectSnap` queries `cursor ± aperture` against
      // it, an aperture orders of magnitude larger than the sagitta.
      //
      // …AND THE GROUP-39 SWEEP (@codex PR #785 round 16). A thickness displaces
      // the entity by `t·N̂`, which for an OBLIQUE group-210 normal has real XY
      // extent, so the centreline box alone UNDER-REPORTS what is drawn: the
      // round-16 witness answered maxX 0 for a polyline whose painted top vertex
      // is at x = 2.886751345948129, and the `osnap-query` broad phase then
      // dropped the entity entirely for a query at that visible vertex. A
      // broad-phase miss is worse than a wrong point — there is no point — and
      // fixing the candidate emitters without this leaves them unreachable. The
      // swept runs come from the ONE derivation the canvas paints from
      // (`scene3d/snap/osnapSweptRuns.ts` over
      // `adapters/geom/polylineThicknessSweep.ts`), so the box is the union of
      // the drawn runs' own vertices rather than a second rule for "how does a
      // box sweep"; a thickness-less polyline yields exactly one run whose
      // points ARE the samples array, and its box is byte-identical to before.
      const samples = polylineCentrelineSamples(
        g.points,
        g.closed,
        g.bulges,
        g.extrusion,
      );
      const runs = osnapPolylineSweptRuns(g, samples);
      const first = runs[0];
      if (runs.length === 1 && first !== undefined) {
        return boundsFromPoints(first.points);
      }
      const sweptPoints: Vec3[] = [];
      for (const run of runs) {
        for (const p of run.points) sweptPoints.push(p);
      }
      return boundsFromPoints(sweptPoints);
    }
    case "mesh-wire":
      // A heavy POLYLINE mesh: the AABB over EVERY wire run's points (one
      // spatial-index entry per entity, exactly as the render path indexes it —
      // the runs are disjoint strokes of ONE entity, not separate entities).
      return boundsFromPoints(g.runs.flatMap((run) => run.points));
    default:
      // Unreachable in practice — `toSceneGeometry` only ever returns one of the 6 kinds above
      // (defensive fallback for a future `SceneGeometry` arm accidentally routed here).
      return boundsFromPoints([]);
  }
}

/**
 * Wrap one entity's `(id, SceneGeometry)` into a `RenderableEntity` the `scene3d/snap`
 * resolvers + `RBushSpatialIndex` accept. `colorHex`/`lineweightWorld`/`linetype`/`zOrder` are
 * INERT placeholders — see the module header for the grep-verified proof that no osnap
 * computation ever reads them. `id`/`spaceId`/`layer`/`type`/`geometry` are the REAL values,
 * copied verbatim off the snapshot ref.
 */
function toRenderableEntity(ref: SnapshotEntityRef, geometry: SceneGeometry): RenderableEntity {
  return {
    id: ref.id,
    spaceId: ref.spaceId,
    // `ref.type` is the real DXF entity-type string; the cast only bridges it to the narrower
    // `EntityPreview["type"]` union RenderableEntity declares (unused by any osnap consumer —
    // see header). This helper is only reached for the 9 `toSceneGeometry`-adapted kinds (Line /
    // Circle / Arc / Ellipse / Spline / LWPOLYLINE / Revision Cloud / Polyline (every
    // subtype — 2-D / 3-D as a `polyline`, polygon / poly-face mesh as a `mesh-wire`) /
    // OLE Frame) — NEITHER `Block` NOR `Table` ever reaches here: both are block
    // references whose FLATTENED CHILDREN are the candidates, handled together in the
    // block-child branch of `projectOsnapQuery` below — all real `entityTypeSchema` values.
    type: ref.type as RenderableEntity["type"],
    bounds: bboxFromSceneGeometry(geometry),
    layer: ref.layer,
    colorHex: "#000000", // inert placeholder — see module header.
    lineweightWorld: 0, // inert placeholder.
    linetype: "ByLayer", // inert placeholder.
    zOrder: 0, // inert placeholder.
    geometry,
  };
}

/**
 * The `invalid-osnap-query` reject page — mirrors `projectBlocks.ts`'s
 * `countSortWithoutFlagReject` shape exactly (a body-level validation this route alone
 * understands: `?osnapX`/`?osnapY`/`?osnapRadius` missing, non-finite, or a non-positive
 * radius). A reject page binds NO route revisions (not a paginated continuation).
 */
function osnapInvalidQueryReject(snapshot: SceneSnapshotRef): StateReadPage {
  return {
    items: [],
    nextOffsetKey: null,
    routeRevisions: {}, // a reject page binds NO route revisions (companion accepts {}).
    resolvedSpaceId: null,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    validationCode: QUERY_VALIDATION_CODES.invalidOsnapQuery,
    total: 0,
  };
}

/**
 * Cross-partition tie-break (GitHub Codex PR #528 Finding 3) — mirrors `resolveObjectSnap`'s OWN
 * internal tie-break loop EXACTLY (`resolveObjectSnap.ts`, the `for (const h of out)` loop near the
 * bottom: `MODE_PRIORITY` first, then `distancePxFromCursor` with a `1e-12` epsilon, then a
 * lexically-smaller `ownerEntityId`), using the now-exported `MODE_PRIORITY` table so this never
 * hardcodes a second copy that could silently drift from the real one. `pixelsPerWorld=1` in this
 * route makes `distancePxFromCursor` numerically the WORLD distance — the SAME quantity
 * `resolveObjectSnap`'s internal `distWorld` compares — so this is not an approximation of the
 * internal tie-break, it is the identical comparison. Returns `true` when `candidate` should REPLACE
 * `current` as the best-so-far.
 */
function isBetterCandidate(candidate: OsnapCandidate, current: OsnapCandidate): boolean {
  const pCandidate = MODE_PRIORITY[candidate.kind];
  const pCurrent = MODE_PRIORITY[current.kind];
  if (pCandidate < pCurrent) {
    return true;
  }
  if (pCandidate > pCurrent) {
    return false;
  }
  if (candidate.distancePxFromCursor < current.distancePxFromCursor - 1e-12) {
    return true;
  }
  return (
    Math.abs(candidate.distancePxFromCursor - current.distancePxFromCursor) <= 1e-12 &&
    candidate.ownerEntityId < current.ownerEntityId
  );
}

/**
 * The `osnap-query` route body (T8). Given `?osnapX=&osnapY=&osnapRadius=` (world units; all
 * REQUIRED, finite, radius > 0 — else `invalid-osnap-query`), returns the SINGLE nearest
 * endpoint/midpoint/intersection candidate among entities in the resolved `?scope` (default
 * active-space; mirrors `entities`/`viewports` space-scoping — NOT document-level), or an
 * EMPTY page (`items: [], total: 0`, no `validationCode`) when none falls within the radius —
 * a genuinely-empty valid result is NEVER conflated with a malformed request (CLAUDE.md
 * ПРИОРИТЕТ #1 fail-closed: distinguishable by construction, since only the invalid-query
 * reject above ever carries a `validationCode`).
 *
 * NEVER paginated (`nextOffsetKey` always `null`; at most 1 item) — a single point query, not
 * a table scan. `routeRevisions: { geometryRevision }` — the ONLY thing a candidate result
 * depends on is entity geometry state (mirrors `entities`'s binding reasoning).
 *
 * Read-only informational context for the AI — this route is DELIBERATELY never registered as
 * an `aiCallable` action; it exists purely so an agent can look BEFORE proposing a
 * CREATE-family / MOVE / etc. command that should snap to existing geometry (T8).
 */
export const projectOsnapQuery: RouteProjector = ({ query, snapshot, resolvedSpaceId }) => {
  const x = query.osnapX;
  const y = query.osnapY;
  const radius = query.osnapRadius;
  if (
    x === undefined ||
    y === undefined ||
    radius === undefined ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(radius) ||
    radius <= 0
  ) {
    return osnapInvalidQueryReject(snapshot);
  }

  // SCOPE — the seam already resolved `?scope` (and neutralized an unknown scope to
  // `entities:[]`), mirroring `entities`/`viewports`. `null` → document scope (all in-scope
  // entities); else filter to the resolved space.
  const scoped =
    resolvedSpaceId === null ? snapshot.entities : snapshot.entities.filter((e) => e.spaceId === resolvedSpaceId);

  // PARTITION BY spaceId (GitHub Codex PR #528 Finding 3) — a single mixed spatial index pooling
  // every in-scope space would let `resolveObjectSnap`'s INTERSECTION pass pair entities from
  // DIFFERENT spaces (model-space and paper-space coordinates are not a single geometric plane), so
  // two lines in different spaces whose coordinates happen to overlap numerically could report a
  // FAKE intersection that exists in neither real space. Grouping by `spaceId` and resolving each
  // partition through its OWN fresh index+resolver call, then merging the (at most N) results,
  // eliminates cross-space pairing by construction. Under the (overwhelming majority) single-space
  // `?scope` case — the default — this naturally degrades to exactly ONE partition = the SAME
  // single index + single `resolveObjectSnap` call as before (no special-casing needed): every
  // entity ref in `scoped` already shares the ONE resolved `spaceId`, and a flattened block-child
  // (Finding 4 below) inherits its parent INSERT's `spaceId` verbatim (`blockFromEntity` /
  // `flattenTypedInsert` thread `spaceId` through unchanged — verified by reading
  // `blockFromEntity.ts`), so it lands in that SAME single partition too.
  const bySpaceId = new Map<string, RenderableEntity[]>();
  const addToPartition = (entity: RenderableEntity): void => {
    const list = bySpaceId.get(entity.spaceId);
    if (list === undefined) {
      bySpaceId.set(entity.spaceId, [entity]);
    } else {
      list.push(entity);
    }
  };
  for (const ref of scoped) {
    if (!OSNAP_QUERY_ENTITY_TYPES.has(ref.type)) {
      continue;
    }
    if (BLOCK_BACKED_ENTITY_TYPES.has(ref.type)) {
      // GitHub Codex PR #528 Finding 4 — a BLOCK REFERENCE is never itself geometry-adapted (it
      // has no `toSceneGeometry` case); its FLATTENED CHILDREN (`captureSnapshot.ts`'s
      // `blockFlattenedChildren` getter — the SAME typed `blockFromEntity` flatten the
      // Category-B render path uses, incl. recursion + MINSERT-grid expansion) are the real
      // candidates, mirroring the interactive canvas OSNAP resolver (which snaps to flattened
      // block-child renderables, not the reference itself). Each child is ALREADY a full
      // `RenderableEntity` with a real `SceneGeometry` (built by the real typed adapters) — no
      // `toSceneGeometry`/`toRenderableEntity` re-adaptation needed, only the same shape-kind
      // filter every other entity gets (`OSNAP_QUERY_CHILD_GEOMETRY_KINDS` — note the LOWERCASE
      // `SceneGeometry.kind` vocabulary, distinct from `SnapshotEntityRef.type`'s uppercase).
      // NOTE: each surviving child's `.id` is the SYNTHETIC id `blockFromEntity` minted
      // (namespaced off the parent reference's id) — `resolveObjectSnap` reports THIS id as
      // `ownerEntityId`, not the top-level entity's own document entity id. Not remapped back to
      // the parent id (would need nontrivial new plumbing) — documented in the seam type's
      // `blockFlattenedChildren` doc comment: "here is a world coordinate + a traceable id", not
      // "here is an entity you can MOVE/select directly".
      //
      // @codex PR #785 round 2, F2 — `"Table"` takes this SAME branch. An ACAD_TABLE is a block
      // reference to its anonymous `*T<N>` graphics block (see `OSNAP_QUERY_ENTITY_TYPES`), so
      // its cell text and grid lines are candidates by exactly this path, with no second "what
      // does a table occupy" derivation anywhere in this route.
      //
      // PRODUCER STATUS (known, documented, NOT a silent gap): the `blockFlattenedChildren`
      // getter is attached by `state/captureSnapshot.ts`'s `toEntityRef`, which today defines it
      // for `entity.type === "Block"` only. Its `"Table"` arm — `tableAsBlockReference(entity)`
      // (the ONE shared `drawing-model` projection `cad-core`'s `select.ts` / `bounds.ts` already
      // resolve a table through) fed to the SAME `blockFromEntity(…)` +
      // `correctRenderableEntityToWorld` pipeline — must land in that module, which is outside
      // this route's own file tree. Until it does, a `"Table"` ref reaches this branch with the
      // facet ABSENT and contributes nothing (the `?? []` below) — the same, never-wrong
      // treatment a `"Block"` INSERT whose definition is missing already gets.
      for (const child of ref.blockFlattenedChildren ?? []) {
        if (OSNAP_QUERY_CHILD_GEOMETRY_KINDS.has(child.geometry.kind)) {
          addToPartition(child);
        }
      }
      continue;
    }
    const geometry = toSceneGeometry(ref);
    if (geometry === null) {
      continue;
    }
    addToPartition(toRenderableEntity(ref, geometry));
  }

  // RESOLVE EACH PARTITION independently, then merge into the single overall best using the SAME
  // priority-then-distance-then-ownerEntityId tie-break `resolveObjectSnap` uses internally
  // (mirrored via the now-exported `MODE_PRIORITY` + `isBetterCandidate` below — never a second,
  // independently-drifting copy of the priority table).
  let best: OsnapCandidate | null = null;
  for (const partitionEntities of bySpaceId.values()) {
    const entityMap = new Map(partitionEntities.map((e) => [e.id, e] as const));
    const spatialIndex = createSpatialIndex((id) => entityMap.get(id));
    spatialIndex.bulkLoad(partitionEntities);
    const candidate = resolveObjectSnap({
      cursorWorld: { x, y },
      cursorClient: { x, y }, // unused by the running-snap path (no extendedOverride supplied).
      pixelsPerWorld: 1, // aperturePx == world units directly (no viewport/camera context here).
      aperturePx: radius,
      activeModes: OSNAP_QUERY_MODES,
      spatialIndex,
      getEntityById: (id) => entityMap.get(id),
    });
    if (candidate === null) {
      continue;
    }
    if (best === null || isBetterCandidate(candidate, best)) {
      best = candidate;
    }
  }

  const items =
    best === null
      ? []
      : [
          {
            mode: best.kind,
            point: { x: best.world.x, y: best.world.y },
            ownerEntityId: best.ownerEntityId,
            // pixelsPerWorld=1 above makes distancePxFromCursor numerically the WORLD distance.
            distanceWorld: best.distancePxFromCursor,
          },
        ];

  return {
    items,
    nextOffsetKey: null,
    routeRevisions: { geometryRevision: snapshot.revisions.geometryRevision },
    resolvedSpaceId,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: items.length,
  };
};
